import { ObservableQuery } from '@apollo/client';

import {
  DocumentDetailQuery,
  DocumentDetailQueryVariables,
  DocumentStatus,
  gql,
} from '@anyx/common-graphql';
import { reportAppError } from '@anyx/shared-utils';

import { useWatchLazyQuery } from './useWatchLazyQuery';

interface DownloadDocumentProps {
  onProcessStart?: () => void;
  onSuccess?: () => void;
  onComplete?: () => void;
  onFailure?: (error?: unknown) => void;
  handleDocumentDownloadUrl: (url: string) => void;
  pollIntervalInMs?: number;
}

const DOCUMENT_DETAIL = gql(/* GraphQL */ `
  query DocumentDetail($documentId: Long!) {
    document(documentId: $documentId) {
      id
      status
      url
    }
  }
`);

export function useDownloadDocument({
  onProcessStart,
  onSuccess,
  onComplete,
  onFailure,
  handleDocumentDownloadUrl,
  pollIntervalInMs = 5000,
}: DownloadDocumentProps) {
  const onSuccessCallback = ({
    data: queryResult,
  }: ObservableQuery.Result<DocumentDetailQuery>) => {
    // Apollo Client v4: data can be undefined when dataState is "empty" or "partial"
    if (!queryResult?.document) {
      return false; // Continue polling
    }

    const { id, status, url } = queryResult.document;

    // Apollo Client v4: properties may be undefined in partial data states
    if (!status) {
      return false; // Continue polling
    }

    switch (status) {
      case DocumentStatus.SUCCESS:
        if (!url) {
          reportAppError(`Encountered Success State with Empty URL for Document Id ${id}`);
          onFailure?.();
          return true;
        }

        handleDocumentDownloadUrl(url);
        onSuccess?.();
        return true;

      case DocumentStatus.FAIL:
        onFailure?.();
        return true;

      default:
        return false;
    }
  };

  const onErrorCallback = (error: unknown) => {
    onFailure?.(error);
  };

  const { listenToWatchedLazyQuery, loading } = useWatchLazyQuery<
    DocumentDetailQuery,
    DocumentDetailQueryVariables
  >({
    query: DOCUMENT_DETAIL,
    options: {
      pollInterval: pollIntervalInMs,
    },
  });

  const handleDownloadDocument = (queryVariables: DocumentDetailQueryVariables) => {
    onProcessStart?.();
    listenToWatchedLazyQuery(queryVariables, onSuccessCallback, onErrorCallback, () => {
      onComplete?.();
    });
  };

  return {
    handleDownloadDocument,
    loading,
  };
}
